import { useEffect, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../assets";

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isHomeDropdownOpen, setIsHomeDropdownOpen] = useState(false);
  const homeDropdownRef = useRef(null);
  const mobileHomeDropdownRef = useRef(null);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const productsDropdownRef = useRef(null);
  const mobileProductsDropdownRef = useRef(null);

  const navItems = [
    { label: "Class", to: "/IELTSTest", hasCaret: true },
    { label: "Placement Test", to: "/PlacementTest" },
    { label: "Blogs", to: "/blog" },
    { label: "About Us", to: "/about-us" },
  ];

  const homeDropdownItems = [
    { label: "Beranda", to: "/" },
  ];

  const productsDropdownItems = [
    { label: "Kelas Private", to: "/produk/KelasPrivate" },
    { label: "Kelas Intensif", to: "/produk/KelasIntensif" },
  ];

  const baseNavClasses =
    "inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 st-focus-ring md:text-[16px]";

  const getNavClasses = ({ isActive }) =>
    `${baseNavClasses} ${
      isActive
        ? "border-[var(--st-color-navy)] st-surface-cream st-text-brand"
        : "border-transparent st-text-body hover:border-[color:rgba(32,42,96,0.25)] hover:st-text-brand"
    }`;

  const toggleMobileMenu = () => {
    if (isMenuOpen) {
      setIsClosing(true);
      return;
    }

    setIsClosing(false);
    setIsMenuOpen(true);
  };

  const closeMobileMenu = () => {
    setIsClosing(true);
  };

  const toggleHomeDropdown = () => {
    setIsHomeDropdownOpen((currentValue) => !currentValue);
    setIsProductsDropdownOpen(false); 
  };

  const toggleProductsDropdown = () => {
    setIsProductsDropdownOpen((currentValue) => !currentValue);
    setIsHomeDropdownOpen(false); 
  };

  const closeProductsDropdown = () => {
    setIsProductsDropdownOpen(false);
  };

  const closeHomeDropdown = () => {
    setIsHomeDropdownOpen(false);
  };

  const scrollToReadinessChecker = () => {
    closeHomeDropdown();
    closeMobileMenu();

    const scrollTarget = () => {
      const targetElement = document.getElementById("onboarding");

      if (!targetElement) {
        return;
      }

      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (pathname !== "/") {
      navigate("/");
      window.setTimeout(scrollTarget, 0);
      return;
    }

    window.requestAnimationFrame(scrollTarget);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    closeMobileMenu();
  };

  useEffect(() => {
    if (isClosing && isMenuOpen) {
      const timer = setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
      }, 220);

      return () => clearTimeout(timer);
    }
  }, [isClosing, isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMobileMenu();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideDesktopDropdown =
        homeDropdownRef.current && homeDropdownRef.current.contains(event.target);
      const clickedInsideMobileDropdown =
        mobileHomeDropdownRef.current && mobileHomeDropdownRef.current.contains(event.target);

      const clickedInsideProductsDesktop =
        productsDropdownRef.current && productsDropdownRef.current.contains(event.target);
      const clickedInsideProductsMobile =
        mobileProductsDropdownRef.current && mobileProductsDropdownRef.current.contains(event.target);

      if (clickedInsideDesktopDropdown || clickedInsideMobileDropdown) {
        return;
      }

      if (clickedInsideProductsDesktop || clickedInsideProductsMobile) {
        return;
      }

      if (isHomeDropdownOpen) {
        closeHomeDropdown();
      }

      if (isProductsDropdownOpen) {
        closeProductsDropdown();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isHomeDropdownOpen, isProductsDropdownOpen]);

  const authAction = user ? (
    <button
      type="button"
      onClick={handleLogout}
      className="st-btn-base st-btn-danger st-focus-ring px-6 font-semibold"
    >
      Logout
    </button>
  ) : (
    <Link
      to="/login"
      className="st-btn-base st-btn-blue st-focus-ring px-6 font-semibold"
    >
      Sign Up
    </Link>
  );

  return (
    <header className="st-surface-cream sticky top-0 z-50 border-b border-[#e8dec7] bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto flex h-20 w-full max-w-6xl items-center justify-between px-4 sm:px-6 2xl:max-w-[120rem] 2xl:px-[14rem]">
        <Link to="/" aria-label="Go to home" className="flex items-center">
          <img src={Logo} alt="Scholars Today Logo" className="h-[116px] w-auto" />
        </Link>

        <div className="hidden items-center gap-2 lg:flex">
          <div className="relative" ref={homeDropdownRef}>
            <button
              type="button"
              onClick={toggleHomeDropdown}
              aria-expanded={isHomeDropdownOpen}
              aria-haspopup="menu"
              className={`${baseNavClasses} border-transparent st-text-body hover:border-[color:rgba(32,42,96,0.25)] hover:st-text-brand`}
            >
              Home
              <span className={`ml-1 text-xs transition-transform duration-200 ${isHomeDropdownOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isHomeDropdownOpen ? (
              <div className="absolute left-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-[#e8dec7] bg-white p-2 shadow-[0_20px_40px_rgba(28,25,23,0.12)]">
                {homeDropdownItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={closeHomeDropdown}
                    className="block rounded-xl px-4 py-3 text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f7efe0] hover:st-text-brand"
                  >
                    {item.label}
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={scrollToReadinessChecker}
                  className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f7efe0] hover:st-text-brand"
                >
                  Readiness Checker
                </button>
              </div>
            ) : null}
          </div>

          <div className="relative ml-2" ref={productsDropdownRef}>
            <button
              type="button"
              onClick={toggleProductsDropdown}
              aria-expanded={isProductsDropdownOpen}
              aria-haspopup="menu"
              className={`${baseNavClasses} border-transparent st-text-body hover:border-[color:rgba(32,42,96,0.25)] hover:st-text-brand`}
            >
              Products
              <span className={`ml-1 text-xs transition-transform duration-200 ${isProductsDropdownOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isProductsDropdownOpen ? (
              <div className="absolute left-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-[#e8dec7] bg-white p-2 shadow-[0_20px_40px_rgba(28,25,23,0.12)]">
                {productsDropdownItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={closeProductsDropdown}
                    className="block rounded-xl px-4 py-3 text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f7efe0] hover:st-text-brand"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>

          {navItems.map((item) => (
            <NavLink key={item.label} to={item.to} end={item.to === "/"} className={getNavClasses}>
              {item.label}
              {item.hasCaret ? <span className="ml-1 text-xs">▼</span> : null}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex">{authAction}</div>

        <div className="lg:hidden">
          <button
            type="button"
            onClick={toggleMobileMenu}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
            className="st-text-brand st-focus-ring rounded-md p-2 transition-colors duration-200 hover:bg-[#f3ebd8]"
          >
            <svg
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMenuOpen ? "M6 6l12 12M18 6L6 18" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div
          className={`st-surface-cream border-t border-[#e8dec7] px-4 pb-5 pt-3 lg:hidden ${
            isClosing ? "animate-slide-up" : "animate-slide-down"
          }`}
        >
          <nav className="mx-auto flex w-full max-w-6xl flex-col gap-2">
            <div ref={mobileHomeDropdownRef} className="overflow-hidden rounded-xl border border-[#e8dec7] bg-[--st-color-cream]">
              <button
                type="button"
                onClick={toggleHomeDropdown}
                aria-expanded={isHomeDropdownOpen}
                className="st-focus-ring flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f8f0de]"
              >
                <span>Home</span>
                <span className={`text-xs transition-transform duration-200 ${isHomeDropdownOpen ? "rotate-180" : ""}`}>▼</span>
              </button>

              {isHomeDropdownOpen ? (
                <div className="border-t border-[#e8dec7] bg-[#fcfaf5] p-2">
                  {homeDropdownItems.map((item) => (
                    <Link
                      key={`mobile-${item.label}`}
                      to={item.to}
                      onClick={() => {
                        closeHomeDropdown();
                        closeMobileMenu();
                      }}
                      className="st-focus-ring block rounded-lg px-4 py-3 text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f3ebd8] hover:st-text-brand"
                    >
                      {item.label}
                    </Link>
                  ))}
                  <button
                    type="button"
                    onClick={scrollToReadinessChecker}
                    className="st-focus-ring block w-full rounded-lg px-4 py-3 text-left text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f3ebd8] hover:st-text-brand"
                  >
                    Readiness Checker
                  </button>
                </div>
              ) : null}
            </div>

            <div ref={mobileProductsDropdownRef} className="mt-2 overflow-hidden rounded-xl border border-[#e8dec7] bg-[--st-color-cream]">
              <button
                type="button"
                onClick={toggleProductsDropdown}
                aria-expanded={isProductsDropdownOpen}
                className="st-focus-ring flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f8f0de]"
              >
                <span>Products</span>
                <span className={`text-xs transition-transform duration-200 ${isProductsDropdownOpen ? "rotate-180" : ""}`}>▼</span>
              </button>

              {isProductsDropdownOpen ? (
                <div className="border-t border-[#e8dec7] bg-[#fcfaf5] p-2">
                  {productsDropdownItems.map((item) => (
                    <Link
                      key={`mobile-${item.label}`}
                      to={item.to}
                      onClick={() => {
                        closeProductsDropdown();
                        closeMobileMenu();
                      }}
                      className="st-focus-ring block rounded-lg px-4 py-3 text-sm font-medium st-text-body transition-colors duration-200 hover:bg-[#f3ebd8] hover:st-text-brand"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>

            {navItems.map((item) => (
              <NavLink
                key={`mobile-${item.label}`}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `st-focus-ring inline-flex items-center justify-between rounded-xl border border-[#e8dec7] px-4 py-3 text-sm font-medium transition-colors duration-200 lg:border-transparent ${
                    isActive
                      ? "bg-[#f3ebd8] st-text-brand"
                      : "st-text-body hover:bg-[#f8f0de]"
                  }`
                }
                onClick={closeMobileMenu}
              >
                <span>{item.label}</span>
                {item.hasCaret ? <span className="text-xs">▼</span> : null}
              </NavLink>
            ))}

            <div className="pt-2">
              {user ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="st-btn-base st-btn-danger st-focus-ring w-full px-5 font-semibold"
                >
                  Logout
                </button>
              ) : (
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="st-btn-base st-btn-blue st-focus-ring w-full px-5 font-semibold"
                >
                  Sign Up
                </Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
